/**
 * "Proovi vestlust", step two: the conversation, and where the AI is on the map.
 *
 * The opening comes first, word for word what a call with this company would
 * say (the test call preview builds it). Every line the owner sends as the
 * callee is answered by the server with the call's own prompt; the reply shows
 * the step it named, and the map beside the chat lights that step. When the AI
 * ends the call the conversation closes, as the phone line would.
 */
import { useEffect, useReducer, useRef, useState } from "react";
import { Bot, Loader2, RotateCcw, Send, User } from "lucide-react";
import { Button, Textarea, cn } from "@wa/ui";
import { FlowMap } from "@wa/widgets/flow-map";
import { readText, type FlowMapDoc } from "@wa/widgets/flow-map/model";
import { useI18n } from "../../../../../i18n/I18nProvider";
import {
  REHEARSE_LIMITS,
  canSend,
  initialRehearse,
  reduceRehearse,
  rehearseCampaign,
  rehearseErrorKey,
  rehearseTooLong,
  requestTurns,
} from "../../../../../lib/aiRehearse";
import type { AiTestCallTarget } from "../../../../../lib/aiTestCallPreview";
import { Note } from "../../../settings/panel/fields";

function Bubble({
  who,
  label,
  text,
  badge,
}: {
  who: "ai" | "callee";
  label: string;
  text: string;
  badge?: string | null;
}) {
  const ai = who === "ai";
  return (
    <div className={cn("flex gap-2", ai ? "justify-start" : "justify-end")}>
      {ai ? <Bot className="mt-1 size-4 shrink-0 text-primary" /> : null}
      <div
        className={cn(
          "max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed",
          ai ? "rounded-tl-sm border border-border bg-muted/40" : "rounded-tr-sm bg-primary text-primary-foreground",
        )}
      >
        <div className={cn("mb-0.5 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide", ai ? "text-muted-foreground" : "opacity-80")}>
          <span>{label}</span>
          {badge ? <span className="rounded-full bg-primary/10 px-1.5 py-px text-primary normal-case tracking-normal">{badge}</span> : null}
        </div>
        {text ? <p className="whitespace-pre-wrap">{text}</p> : null}
      </div>
      {!ai ? <User className="mt-1 size-4 shrink-0 text-muted-foreground" /> : null}
    </div>
  );
}

export function RehearseChat({
  campaignId,
  target,
  opening,
  doc,
}: {
  campaignId: string;
  target: AiTestCallTarget;
  /** The opening a call with this company would say. */
  opening: string;
  /** The call flow map on screen: what the AI follows here, and what the map beside the chat draws. */
  doc: FlowMapDoc;
}) {
  const { t, locale } = useI18n();
  const [state, dispatch] = useReducer(reduceRehearse, undefined, initialRehearse);
  const [draft, setDraft] = useState("");
  const log = useRef<HTMLDivElement>(null);
  // The request the conversation waits for, read after an await: a restart in between makes an answer stale.
  const latestSeq = useRef(state.seq);
  latestSeq.current = state.seq;
  const hasCourse = doc.nodes.some((n) => n.kind !== "note" && n.kind !== "group");

  useEffect(() => {
    const el = log.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [state.turns.length, state.pending]);

  async function send() {
    const text = draft.trim();
    if (!canSend(state, text)) return;
    const turns = requestTurns(state);
    const seq = state.seq + 1;
    dispatch({ type: "send", text });
    setDraft("");
    try {
      const res = await rehearseCampaign(campaignId, {
        ...(target.regCode ? { regCode: target.regCode } : {}),
        ...(target.domain ? { domain: target.domain } : {}),
        doc,
        turns,
        message: text,
      });
      dispatch({ type: "reply", reply: res.reply, seq });
    } catch (err) {
      if (latestSeq.current !== seq) return;
      dispatch({ type: "failed", error: rehearseErrorKey(err), seq });
      setDraft(text);
    }
  }

  const marks = state.activeNode ? { [state.activeNode]: "active" as const } : undefined;
  // Named in words as well: on a long map the lit step can be out of the canvas's view.
  const activeTitle = state.activeNode ? readText(doc.nodes.find((n) => n.id === state.activeNode)?.title).trim() : "";
  const tooLong = rehearseTooLong(state);

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,420px)]">
      <div className="flex min-w-0 flex-col gap-3">
        <div ref={log} className="max-h-[48dvh] min-h-[220px] space-y-3 overflow-y-auto rounded-xl border border-border p-3" aria-live="polite">
          <Bubble who="ai" label={t("aiCalls.rehearse.chat.opening")} text={opening} />
          {state.turns.map((turn, i) =>
            turn.role === "callee" ? (
              <Bubble key={i} who="callee" label={t("aiCalls.rehearse.chat.you")} text={turn.text} />
            ) : (
              <Bubble
                key={i}
                who="ai"
                label={t("aiCalls.rehearse.chat.ai")}
                text={turn.text}
                badge={turn.step ? t("aiCalls.rehearse.chat.step", { label: turn.step.label }) : null}
              />
            ),
          )}
          {state.pending !== null ? (
            <>
              <Bubble who="callee" label={t("aiCalls.rehearse.chat.you")} text={state.pending} />
              <p className="crm-sub flex items-center gap-2 text-sm">
                <Loader2 className="size-3.5 animate-spin" />
                {t("aiCalls.rehearse.chat.thinking")}
              </p>
            </>
          ) : null}
        </div>

        {state.closed === "end" ? <Note tone="info">{t("aiCalls.rehearse.chat.ended")}</Note> : null}
        {state.closed === "handoff" ? <Note tone="info">{t("aiCalls.rehearse.chat.handoff")}</Note> : null}
        {tooLong ? <Note tone="warn">{t("aiCalls.rehearse.chat.tooLong")}</Note> : null}
        {state.error ? (
          <Note tone="bad" live>
            {t(`aiCalls.rehearse.error.${state.error}`)}
          </Note>
        ) : null}

        <div className="space-y-2">
          <Textarea
            value={draft}
            rows={2}
            maxLength={REHEARSE_LIMITS.text}
            placeholder={t("aiCalls.rehearse.chat.placeholder")}
            aria-label={t("aiCalls.rehearse.chat.placeholder")}
            disabled={state.closed !== null || tooLong}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                void send();
              }
            }}
          />
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Button variant="ghost" size="sm" onClick={() => dispatch({ type: "reset" })} disabled={state.turns.length === 0 || state.pending !== null}>
              <RotateCcw className="size-3.5" />
              {t("aiCalls.rehearse.chat.restart")}
            </Button>
            <Button size="sm" onClick={() => void send()} disabled={!canSend(state, draft)} loading={state.pending !== null}>
              <Send className="size-3.5" />
              {t("aiCalls.rehearse.chat.send")}
            </Button>
          </div>
        </div>
      </div>

      <div className="min-w-0 space-y-2">
        <p className="crm-lab !mt-0">{t("aiCalls.rehearse.map.title")}</p>
        {hasCourse ? (
          <>
            <FlowMap value={doc} locale={locale} height={340} showViewToggle={false} nodeMarks={marks} ariaLabel={t("aiCalls.rehearse.map.title")} />
            <p className="crm-sub text-xs">
              {activeTitle ? t("aiCalls.rehearse.map.at", { title: activeTitle }) : t("aiCalls.rehearse.map.none")}
            </p>
          </>
        ) : (
          <p className="crm-sub text-sm">{t("aiCalls.rehearse.chat.noCourse")}</p>
        )}
      </div>
    </div>
  );
}
